import React, {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import {
  FaArrowLeft,
  FaClock,
  FaEye,
  FaLeaf,
  FaMinus,
  FaPlus,
  FaSearch,
  FaTimes,
  FaUtensils,
} from "react-icons/fa";
import { getMenuByRestaurant } from "../../../api/menuApi";
import { useSignalREvent } from "../../../hooks/useSignalREvent";
import { CartItem } from "./CartPage";
import { CustomerDetails } from "../types";

const VegIcon = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="#16a34a" strokeWidth="2" />
    <circle cx="12" cy="12" r="5" fill="#16a34a" />
  </svg>
);

const NonVegIcon = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="#9a3412" strokeWidth="2" />
    <path d="M12 7L17 17H7L12 7Z" fill="#9a3412" />
  </svg>
);

const SUBCATEGORY_OPTIONS = [
  { type: "all", label: "All", icon: <FaUtensils size={10} /> },
  { type: "veg", label: "Veg", icon: VegIcon },
  { type: "nonveg", label: "Non-Veg", icon: NonVegIcon },
  { type: "vegan", label: "Vegan", icon: <FaLeaf size={10} /> },
  {
    type: "jain",
    label: "Jain",
    icon: <span className="text-[9px] font-black">J</span>,
  },
];

interface MenuItem {
  id: string;
  restaurantGuid: string;
  name: string;
  cuisineType?: string[] | null;
  shortDescription?: string | null;
  description: string | null;
  subcategory?: string | null;
  category: string;
  dishTags?: string[] | null;
  spiceLevel?: number | null;
  price: number;
  discountPrice?: number | null;
  taxesApplicable: boolean;
  servingSize?: string | null;
  availableDays?: string[] | null;
  availableFrom?: string | null;
  availableTo?: string | null;
  keyIngredients?: string[] | null;
  allergens?: string[] | null;
  imageUrl?: string | null;
  itemImage?: string | null;
  isAvailable: boolean;
  isBestseller?: boolean | null;
  estimatedTimeInMin?: number | null;
}

type Props = {
  customerDetails?: CustomerDetails;
  tableId: string;
  tableName?: string;
  restaurantGuid: string;
  cartItems: CartItem[];
  onBack: () => void;
  onAddToCart: (item: MenuItem, quantity: number) => void;
  onUpdateCartQuantity: (itemId: string, quantity: number) => void;
};

const getDietTone = (subcategory?: string | null) => {
  const normalized = subcategory?.toLowerCase() || "";

  if (normalized === "veg") {
    return "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300";
  }

  if (normalized === "nonveg" || normalized === "non-veg") {
    return "bg-orange-50 text-orange-700 dark:bg-orange-500/10 dark:text-orange-300";
  }

  if (normalized === "vegan") {
    return "bg-lime-50 text-lime-700 dark:bg-lime-500/10 dark:text-lime-300";
  }

  if (normalized === "jain") {
    return "bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300";
  }

  return "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300";
};

const Badge = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${className}`}>
    {children}
  </span>
);

const QuantityControl = memo(
  ({
    quantity,
    onIncrement,
    onDecrement,
  }: {
    quantity: number;
    onIncrement: () => void;
    onDecrement: () => void;
  }) => (
    <div className="flex items-center gap-1 rounded-full bg-slate-100 p-1 dark:bg-slate-800">
      <button
        onClick={onDecrement}
        className="flex h-7 w-7 items-center justify-center rounded-full bg-white text-slate-700 transition active:scale-90 dark:bg-slate-700 dark:text-white"
      >
        <FaMinus size={9} />
      </button>

      <span className="min-w-[18px] text-center text-[11px] font-bold text-slate-900 dark:text-white">
        {quantity}
      </span>

      <button
        onClick={onIncrement}
        className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-500 text-white transition active:scale-90"
      >
        <FaPlus size={9} />
      </button>
    </div>
  )
);
const MenuCard = memo(
  ({ item, quantity, onAdd, onIncrement, onDecrement }: {
    item: MenuItem; quantity: number;
    onAdd: () => void; onIncrement: () => void; onDecrement: () => void;
  }) => {
    const [showImage, setShowImage] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const sellingPrice = item.discountPrice || item.price;
    const isDiscounted = !!item.discountPrice && item.discountPrice < item.price;
    const imageUrl = item.imageUrl || item.itemImage;
    const description = item.shortDescription || item.description || "Freshly prepared";
    const shouldTrim = description.length > 75;

    return (
      <div className="rounded-[1.15rem] border border-orange-100 bg-white/95 p-2 shadow-[0_8px_24px_rgba(251,146,60,0.13)] transition-all duration-200 active:scale-[0.985] dark:border-orange-500/15 dark:bg-slate-900/95 dark:shadow-[0_8px_24px_rgba(251,146,60,0.07)] sm:rounded-[1.45rem] sm:p-2.5">
        <div className="grid grid-cols-[60px,minmax(0,1fr),72px] gap-2 sm:grid-cols-[76px,minmax(0,1fr),88px] sm:gap-3">

          <div className="relative flex h-[60px] w-[60px] shrink-0 items-center justify-center overflow-hidden rounded-[0.95rem] bg-slate-100 dark:bg-slate-800 sm:h-[76px] sm:w-[76px] sm:rounded-[1.15rem]">
            {showImage && imageUrl ? (
              <img src={imageUrl} alt={item.name} loading="lazy" className="h-full w-full object-cover" />
            ) : (
              <button onClick={() => setShowImage(true)} className="flex flex-col items-center justify-center gap-0.5 text-slate-400 transition-all active:scale-90">
                <FaEye size={10} />
                <span className="text-[8px] font-semibold sm:text-[9px]">Show</span>
              </button>
            )}
          </div>

          <div className="min-w-0">
            <div className="mb-1 flex flex-wrap items-center gap-1">
              {item.subcategory && (
                <Badge className={`${getDietTone(item.subcategory)} px-1.5 py-[3px] text-[8px] font-semibold sm:text-[9px]`}>
                  {item.subcategory}
                </Badge>
              )}
              {item.category && (
                <Badge className="bg-slate-100 px-1.5 py-[3px] text-[8px] text-slate-600 dark:bg-slate-800 dark:text-slate-300 sm:text-[9px]">
                  {item.category}
                </Badge>
              )}
              {item.estimatedTimeInMin && (
                <Badge className="bg-slate-100 px-1.5 py-[3px] text-[8px] text-slate-600 dark:bg-slate-800 dark:text-slate-300 sm:text-[9px]">
                  <span className="flex items-center gap-0.5"><FaClock size={7} />{item.estimatedTimeInMin}m</span>
                </Badge>
              )}
            </div>
            <h3 className="line-clamp-1 text-[12.5px] font-bold leading-4 text-slate-900 dark:text-white sm:text-[14px] sm:leading-5">{item.name}</h3>
            <div className="mt-0.5">
              <p className={`text-[9.5px] leading-[13px] text-slate-500 dark:text-slate-400 sm:text-[10.5px] sm:leading-[15px] ${expanded ? "" : "line-clamp-2"}`}>
                {description}
              </p>
              {shouldTrim && (
                <button onClick={() => setExpanded(p => !p)} className="mt-0.5 text-[8px] font-semibold text-orange-500 hover:opacity-80 sm:text-[9px]">
                  {expanded ? "Less" : "More"}
                </button>
              )}
            </div>
          </div>

          <div className="flex min-w-[72px] flex-col items-center justify-center gap-2 self-stretch sm:min-w-[88px]">
            <div className="flex flex-1 items-center justify-center">
              <div className="flex flex-col items-center leading-none">
                <span className="text-[15px] font-black tracking-tight text-slate-900 dark:text-white sm:text-[18px] lg:text-[20px]">₹{sellingPrice.toFixed(0)}</span>
                {isDiscounted && <span className="mt-1 text-[8px] text-slate-400 line-through sm:text-[9px]">₹{item.price.toFixed(0)}</span>}
              </div>
            </div>
            <div className="flex items-center justify-center">
              {quantity > 0 ? (
                <div className="scale-[0.9] sm:scale-[0.96]">
                  <QuantityControl quantity={quantity} onIncrement={onIncrement} onDecrement={onDecrement} />
                </div>
              ) : (
                <button onClick={onAdd} className="h-7 min-w-[58px] rounded-full bg-orange-500 px-3 text-[9px] font-bold text-white shadow-sm transition-all active:scale-90 sm:h-8 sm:min-w-[66px] sm:text-[10px]">
                  Add
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);
const NewOrderPage: React.FC<Props> = ({
  customerDetails = {},
  tableId,
  tableName = "",
  restaurantGuid,
  cartItems,
  onBack,
  onAddToCart,
  onUpdateCartQuantity,
}) => {
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedSubcategory, setSelectedSubcategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  const scrollRef = useRef(0);
  const deferredSearch = useDeferredValue(searchTerm);

  // eslint-disable-next-line
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    const handleScroll = () => {
      const current = window.scrollY;

      if (current > scrollRef.current && current > 60) {
        setShowFilters(false);
      } else {
        setShowFilters(true);
      }

      scrollRef.current = current;
    };

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const categories = useMemo(() => {
    return ["All", ...new Set(menuItems.map((item) => item.category || "Others"))];
  }, [menuItems]);

  const cartMap = useMemo(
    () => new Map(cartItems.map((item) => [item.id, item])),
    [cartItems]
  );

  const fetchMenu = useCallback(
    async (initial = false) => {
      if (!restaurantGuid) {
        setError("Restaurant not found");
        return;
      }

      try {
        if (initial) {
          setLoading(true);
        }

        const menuData = await getMenuByRestaurant(restaurantGuid);

        if (!Array.isArray(menuData)) {
          throw new Error("Invalid menu data");
        }

        startTransition(() => {
          setMenuItems(menuData);
        });

        localStorage.setItem("menu", JSON.stringify(menuData));
      } catch (err) {
        console.error(err);
        setError("Failed to load menu");
      } finally {
        if (initial) {
          setLoading(false);
        }
      }
    },
    [restaurantGuid]
  );

  useEffect(() => {
    const cached = localStorage.getItem("menu");

    if (cached) {
      try {
        const parsed = JSON.parse(cached);

        if (Array.isArray(parsed)) {
          setMenuItems(parsed);
        }
      } catch { }
    }

    fetchMenu(true);
  }, [fetchMenu]);

  useSignalREvent("CustomerMenuUpdate", "CustomerMenu", () => fetchMenu(false));

  const filteredItems = useMemo(() => {
    const search = deferredSearch.toLowerCase();

    return menuItems.filter((item) => {
      const matchesCategory = selectedCategory === "All" || item.category === selectedCategory;
      const normalizedSub = item.subcategory?.toLowerCase().replace("-", "") || "";
      const selectedSub = selectedSubcategory.toLowerCase().replace("-", "");
      const matchesSubcategory = selectedSubcategory === "All" || normalizedSub === selectedSub;
      const matchesSearch =
        item.name.toLowerCase().includes(search) ||
        item.description?.toLowerCase().includes(search) ||
        item.shortDescription?.toLowerCase().includes(search);

      return matchesCategory && matchesSubcategory && matchesSearch && item.isAvailable;
    });
  }, [menuItems, selectedCategory, selectedSubcategory, deferredSearch]);

  const handleIncrement = (id: string) => {
    const item = cartMap.get(id);

    if (!item) {
      return;
    }

    onUpdateCartQuantity(id, item.quantity + 1);
  };

  const handleDecrement = (id: string) => {
    const item = cartMap.get(id);

    if (!item) {
      return;
    }

    if (item.quantity === 1) {
      onUpdateCartQuantity(id, 0);
      return;
    }

    onUpdateCartQuantity(id, item.quantity - 1);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-9 w-9 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="rounded-3xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
          <p className="text-sm text-red-500">{error}</p>

          <button
            onClick={() => fetchMenu(true)}
            className="mt-3 rounded-full bg-orange-500 px-4 py-2 text-xs font-bold text-white"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen overflow-x-hidden text-slate-900 dark:text-white"
      style={{
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <header className="sticky top-0 z-30 border-b border-slate-200/60 bg-gray-100/90 backdrop-blur-xl dark:border-slate-800 dark:bg-gray-900/90">
        <div className="mx-auto max-w-7xl px-3 py-3 sm:px-4 sm:py-4">
          <div className="flex items-start gap-3">
            <button
              onClick={onBack}
              className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-slate-700 shadow-sm ring-1 ring-slate-200 transition active:scale-90 dark:bg-slate-900 dark:text-white dark:ring-slate-700"
            >
              <FaArrowLeft size={14} />
            </button>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                <h1 className="min-w-0 flex-1 basis-[12rem] truncate text-base font-black text-slate-950 dark:text-slate-100 sm:text-xl">
                  {tableName || tableId}
                </h1>

                <span className="max-w-full truncate rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-700 sm:max-w-[45%]">
                  {customerDetails.customerName || "Guest"}
                </span>
              </div>
            </div>
          </div>

          <div
            className={`overflow-hidden transition-all duration-300 ${showFilters ? "max-h-[180px] opacity-100 pt-3" : "max-h-0 opacity-0"
              }`}
          >
            <div className="relative">
              <FaSearch
                size={11}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search dishes..."
                className="h-10 w-full rounded-2xl border border-slate-200 bg-white pl-9 pr-9 text-[12px] outline-none transition focus:border-orange-400 dark:border-slate-800 dark:bg-slate-900 dark:text-white"
              />

              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-slate-400"
                >
                  <FaTimes size={9} />
                </button>
              )}
            </div>

            <div className="mt-2 flex gap-1.5 overflow-x-auto pb-1">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`shrink-0 rounded-full px-3 py-1.5 text-[11px] font-bold transition-all active:scale-95 ${selectedCategory === category
                    ? "bg-slate-950 text-white dark:bg-white dark:text-slate-950"
                    : "bg-white text-slate-600 dark:bg-slate-900 dark:text-slate-300"
                    }`}
                >
                  {category}
                </button>
              ))}
            </div>

            <div className="mt-1.5 flex gap-1.5 overflow-x-auto pb-1">
              {SUBCATEGORY_OPTIONS.map((option) => (
                <button
                  key={option.type}
                  onClick={() => setSelectedSubcategory(option.label)}
                  className={`inline-flex shrink-0 items-center gap-1 rounded-full px-3 py-1.5 text-[11px] font-bold transition-all active:scale-95 ${selectedSubcategory.toLowerCase() === option.label.toLowerCase()
                    ? "bg-orange-500 text-white"
                    : "bg-orange-50 text-orange-700 dark:bg-orange-500/10 dark:text-orange-300"
                    }`}
                >
                  {option.icon}
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-3 py-3 pb-[100px] sm:px-4 sm:py-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-baseline gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Dishes</span>
            <span className="text-slate-300 dark:text-slate-600">:</span>
            <span className="text-[12px] font-semibold text-slate-700 dark:text-slate-300">{filteredItems.length} items</span>
          </div>
          {isPending && <div className="text-[11px] text-slate-400">Updating...</div>}
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {filteredItems.map((item) => {
            const cartItem = cartMap.get(item.id);

            return (
              <MenuCard
                key={item.id}
                item={item}
                quantity={cartItem?.quantity || 0}
                onAdd={() => onAddToCart(item, 1)}
                onIncrement={() => handleIncrement(item.id)}
                onDecrement={() => handleDecrement(item.id)}
              />
            );
          })}
        </div>

        {filteredItems.length === 0 && (
          <div className="mt-10 flex flex-col items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-white/70 px-6 py-10 text-center dark:border-slate-700 dark:bg-slate-900/50">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-50 text-orange-500 dark:bg-orange-500/10 dark:text-orange-300">
              <FaSearch size={14} />
            </div>

            <p className="mt-3 text-[13px] font-semibold text-slate-700 dark:text-slate-300">
              No dishes found
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default NewOrderPage;
